
import { useEffect } from 'react';
import { CampaignFormData } from '../types';
import { useWorkflowAnalysisSimple } from './useWorkflowAnalysisSimple';
import { useWorkflowPageState } from './utils/workflowPageState';
import { initializeCampaign, INITIAL_STEPS } from './utils/campaignInitializer';

interface UseWorkflowPageProps {
  formData: CampaignFormData;
  userId: string;
}

export const useWorkflowPage = ({ formData, userId }: UseWorkflowPageProps) => {
  const state = useWorkflowPageState();
  const {
    initializationError,
    isInitializing,
    initializationAttempts,
    setInitializationAttempts,
    setShouldStartAnalysis,
    setShowSummary,
    setConsolidatedReport,
    setInitializationError,
    setIsInitializing
  } = state;

  const workflowAnalysis = useWorkflowAnalysisSimple({ formData, initialSteps: INITIAL_STEPS });
  const {
    campaignId,
    setCampaignId,
    currentStep,
    steps,
    shouldStartAnalysis,
    processStep,
    restartAnalysis,
    isProcessing,
    isComplete
  } = workflowAnalysis;

  // Add defensive check for steps - ensure it's always an array
  const safeSteps = Array.isArray(steps) ? steps : INITIAL_STEPS;

  // Campaign initialization effect
  useEffect(() => {
    if (userId && formData && !campaignId) {
      const handleInitialization = async () => {
        // Retry logic with delay
        if (initializationAttempts < 2 && initializationError) {
          console.log('Retrying initialization in 3 seconds...');
          setTimeout(() => {
            initializeCampaign({
              userId,
              formData,
              initializationAttempts,
              setInitializationAttempts,
              setCampaignId,
              setInitializationError,
              workflowAnalysis,
              setShouldStartAnalysis,
              setShowSummary,
              setConsolidatedReport,
              setIsInitializing
            });
          }, 3000);
        } else {
          await initializeCampaign({
            userId,
            formData,
            initializationAttempts,
            setInitializationAttempts,
            setCampaignId,
            setInitializationError,
            workflowAnalysis,
            setShouldStartAnalysis,
            setShowSummary,
            setConsolidatedReport,
            setIsInitializing
          });
        }
      };

      handleInitialization();
    }
  }, [userId, formData, initializationAttempts, campaignId]);

  // Processing effect
  useEffect(() => {
    console.log('🔄 Processing effect triggered with state:', {
      campaignId: !!campaignId,
      currentStep,
      totalSteps: safeSteps.length,
      shouldStartAnalysis,
      isProcessing,
      isComplete,
      isInitializing,
      initializationError: !!initializationError
    });

    // Only start processing if initialization was successful
    if (initializationError) {
      console.log('❌ Skipping processing due to initialization error');
      return;
    }
    
    if (isInitializing) {
      console.log('⏳ Skipping processing - still initializing');
      return;
    }

    const timer = setTimeout(() => {
      const shouldProcess = campaignId && 
                           currentStep <= safeSteps.length && 
                           shouldStartAnalysis && 
                           !isProcessing && 
                           !isComplete;
      
      console.log('⏰ Timer triggered - should process?', shouldProcess, {
        hasCampaignId: !!campaignId,
        stepInRange: currentStep <= safeSteps.length,
        shouldStart: shouldStartAnalysis,
        notProcessing: !isProcessing,
        notComplete: !isComplete
      });

      if (shouldProcess) {
        console.log('🚀 Starting processStep for step:', currentStep);
        processStep();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [campaignId, currentStep, shouldStartAnalysis, initializationError, isInitializing, safeSteps.length, isProcessing, isComplete]);

  return {
    ...state,
    workflowAnalysis: {
      ...workflowAnalysis,
      steps: safeSteps // Always ensure we return a safe array
    },
    restartAnalysis
  };
};
